/*
  Routines — Phase 2 real page. Create, view, run, archive. AI-assist suggests
  a starter routine from the user's existing prescriptions & todos.

  Hidden behind the `feature_routines` admin flag.
*/
import { useEffect, useState } from "react";
import {
  Layers, Sparkles, Plus, Trash2, Play, Check, Clock, Sun, Moon, Sunrise,
} from "lucide-react";
import { toast } from "sonner";
import { phase2Api } from "../lib/phase2Api";

const TIME_META = {
  morning:   { label: "Morning",   icon: Sunrise },
  afternoon: { label: "Afternoon", icon: Sun },
  evening:   { label: "Evening",   icon: Moon },
  custom:    { label: "Custom",    icon: Clock },
};

export default function RoutinesPage() {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null); // draft routine object
  const [suggesting, setSuggesting] = useState(null);
  const [running, setRunning] = useState(null);   // {routineId, stepIndex, done:Set}

  async function load() {
    setLoading(true);
    try { setRoutines(await phase2Api.listRoutines()); }
    catch { /* noop */ }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function suggest(time_of_day) {
    setSuggesting(time_of_day);
    try {
      const draft = await phase2Api.suggestRoutine(time_of_day);
      setCreating(draft);
    } catch (e) { toast.error("Couldn't suggest — try again"); }
    finally { setSuggesting(null); }
  }

  function startBlank(time_of_day = "morning") {
    setCreating({ name: "", time_of_day, custom_time: "", steps: [] });
  }

  async function saveDraft() {
    if (!creating?.name?.trim()) { toast.error("Give it a name"); return; }
    try {
      const created = await phase2Api.createRoutine({
        name: creating.name.trim(),
        time_of_day: creating.time_of_day || "morning",
        custom_time: creating.custom_time || null,
        steps: (creating.steps || []).filter(s => s.title?.trim()),
      });
      setRoutines(xs => [created, ...xs]);
      setCreating(null);
      toast.success("Routine saved");
    } catch (e) { toast.error("Save failed"); }
  }

  async function archive(id) {
    try {
      await phase2Api.updateRoutine(id, { archived: true });
      setRoutines(xs => xs.filter(r => r.id !== id));
      toast.success("Archived");
    } catch { toast.error("Couldn't archive"); }
  }

  function runRoutine(r) {
    setRunning({ routineId: r.id, done: new Set() });
  }
  function toggleStep(idx) {
    setRunning(s => {
      const next = new Set(s.done);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return { ...s, done: next };
    });
  }
  const activeRun = running && routines.find(r => r.id === running.routineId);

  return (
    <div className="animate-fade-up max-w-3xl">
      <div className="mb-6">
        <span className="label-caps">Your rituals</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Smart routines.</h1>
        <p className="mt-2 text-sm sm:text-base" style={{ color: "var(--text-muted)" }}>
          Bundle meds, tasks, and reminders into morning or evening rituals — so your day runs itself.
        </p>
      </div>

      {/* Quick actions */}
      <div className="card-tactile rounded-2xl p-5 mb-6" data-testid="routines-ai-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-[color:var(--primary-ink)]" />
          </div>
          <div>
            <div className="font-display font-bold">Build one with AI</div>
            <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Suggests steps from your existing prescriptions and todos.</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {["morning", "afternoon", "evening"].map(t => {
            const Icon = TIME_META[t].icon;
            return (
              <button key={t} onClick={() => suggest(t)} disabled={suggesting === t}
                data-testid={`suggest-${t}`}
                className="inline-flex items-center gap-2 px-3 h-10 rounded-lg text-sm font-semibold border hover:border-[color:var(--primary)] disabled:opacity-50"
                style={{ borderColor: "var(--border-subtle)" }}
              >
                <Icon className="h-4 w-4" /> {suggesting === t ? "Thinking…" : `Suggest ${TIME_META[t].label}`}
              </button>
            );
          })}
          <button onClick={() => startBlank("morning")} data-testid="start-blank"
            className="inline-flex items-center gap-2 px-3 h-10 rounded-lg text-sm font-semibold text-white"
            style={{ background: "var(--primary)" }}>
            <Plus className="h-4 w-4" /> Blank routine
          </button>
        </div>
      </div>

      {/* Draft editor */}
      {creating && (
        <div className="card-tactile rounded-2xl p-5 mb-6" data-testid="routines-draft">
          <div className="label-caps mb-2">New routine</div>
          <input
            value={creating.name}
            onChange={(e) => setCreating(c => ({ ...c, name: e.target.value }))}
            placeholder="Name (e.g. Morning)"
            data-testid="draft-name"
            className="w-full px-3 py-2 rounded-md border bg-[color:var(--surface)] focus:outline-none focus:border-[color:var(--primary)]"
            style={{ borderColor: "var(--border-subtle)" }}
          />
          <div className="mt-3 flex flex-wrap gap-1.5">
            {["morning", "afternoon", "evening", "custom"].map(t => (
              <button key={t} onClick={() => setCreating(c => ({ ...c, time_of_day: t }))}
                className={`text-xs px-2.5 py-1 rounded-full border ${creating.time_of_day === t ? "text-white border-transparent" : "hover:border-[color:var(--primary)]"}`}
                style={creating.time_of_day === t ? { background: "var(--primary)" } : { borderColor: "var(--border-subtle)" }}>
                {TIME_META[t].label}
              </button>
            ))}
          </div>
          <ul className="mt-4 space-y-2">
            {(creating.steps || []).map((s, i) => (
              <li key={i} className="flex items-center gap-2">
                <input
                  value={s.title}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCreating(c => ({ ...c, steps: c.steps.map((x, j) => j === i ? { ...x, title: v } : x) }));
                  }}
                  placeholder="Step title"
                  className="flex-1 px-3 py-2 rounded-md border bg-[color:var(--surface)] text-sm focus:outline-none focus:border-[color:var(--primary)]"
                  style={{ borderColor: "var(--border-subtle)" }}
                />
                <input
                  type="number"
                  min={1} max={60}
                  value={s.duration_minutes || ""}
                  onChange={(e) => {
                    const v = Number(e.target.value) || null;
                    setCreating(c => ({ ...c, steps: c.steps.map((x, j) => j === i ? { ...x, duration_minutes: v } : x) }));
                  }}
                  placeholder="min"
                  className="w-20 px-2 py-2 rounded-md border bg-[color:var(--surface)] text-sm text-center"
                  style={{ borderColor: "var(--border-subtle)" }}
                />
                <button onClick={() => setCreating(c => ({ ...c, steps: c.steps.filter((_, j) => j !== i) }))}
                  className="p-2 rounded hover:bg-[color:var(--surface-2)]">
                  <Trash2 className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
                </button>
              </li>
            ))}
          </ul>
          <button onClick={() => setCreating(c => ({ ...c, steps: [...(c.steps || []), { ref_type: "free", title: "", duration_minutes: null }] }))}
            data-testid="add-step"
            className="mt-2 text-xs font-semibold inline-flex items-center gap-1 hover:underline"
            style={{ color: "var(--primary-ink)" }}>
            <Plus className="h-3 w-3" /> Add step
          </button>
          <div className="mt-4 flex gap-2">
            <button onClick={saveDraft} data-testid="save-draft"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold text-white"
              style={{ background: "var(--primary)" }}>
              <Check className="h-4 w-4" /> Save routine
            </button>
            <button onClick={() => setCreating(null)}
              className="px-4 h-10 rounded-lg text-sm font-semibold border hover:bg-[color:var(--surface-2)]"
              style={{ borderColor: "var(--border-subtle)" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Routine list */}
      {loading ? (
        <div className="text-sm py-8 text-center" style={{ color: "var(--text-muted)" }}>Loading…</div>
      ) : routines.length === 0 && !creating ? (
        <div className="card-tactile rounded-2xl p-8 text-center">
          <Layers className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>No routines yet. Build one above.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="routines-list">
          {routines.map(r => {
            const Icon = TIME_META[r.time_of_day]?.icon || Clock;
            return (
              <li key={r.id} className="card-tactile rounded-xl p-4" data-testid="routine-row">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg shrink-0 bg-[color:var(--primary-soft)] flex items-center justify-center">
                    <Icon className="h-4 w-4 text-[color:var(--primary-ink)]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold truncate">{r.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                      {TIME_META[r.time_of_day]?.label} · {r.steps?.length || 0} step{r.steps?.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <button onClick={() => runRoutine(r)} data-testid={`run-${r.id}`}
                    className="inline-flex items-center gap-1 px-3 h-8 rounded-md text-xs font-semibold text-white"
                    style={{ background: "var(--primary)" }}>
                    <Play className="h-3 w-3" /> Run
                  </button>
                  <button onClick={() => archive(r.id)} className="p-1.5 rounded hover:bg-[color:var(--surface-2)]" title="Archive">
                    <Trash2 className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
                  </button>
                </div>
                {activeRun?.id === r.id && (
                  <ol className="mt-4 space-y-2">
                    {r.steps.map((s, i) => {
                      const done = running.done.has(i);
                      return (
                        <li key={i}>
                          <button onClick={() => toggleStep(i)}
                            className="w-full text-left flex items-center gap-3 p-3 rounded-lg border hover:bg-[color:var(--surface-2)] transition-colors"
                            style={{ borderColor: "var(--border-subtle)" }}>
                            <span className={`h-5 w-5 shrink-0 rounded-full border flex items-center justify-center ${done ? "bg-[color:var(--primary)] border-transparent text-white" : ""}`}
                              style={done ? {} : { borderColor: "var(--border-subtle)" }}>
                              {done && <Check className="h-3 w-3" />}
                            </span>
                            <span className={`text-sm flex-1 ${done ? "line-through" : ""}`} style={done ? { color: "var(--text-muted)" } : {}}>
                              {s.title}
                            </span>
                            {s.duration_minutes && <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{s.duration_minutes} min</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
